<!-- src/components/base/BaseHorizontalList.vue -->
<script setup lang="ts">
import ChevronLeftIcon from '@/assets/svgs/chevron_left.svg'
import ChevronRightIcon from '@/assets/svgs/chevron_right.svg'
import { useIsMobile } from '@/composables/useMediaQuery'
import { computed, onMounted, ref } from 'vue'

// 네비게이션 버튼 표시 여부 설정
const props = defineProps<{
  title?: string
  showNavigation?: boolean
  marginTop?: number
  gap?: number
  scrollWidth?: number
}>()

const isMobile = useIsMobile()

const computedMarginTop = computed(() => {
  if (props.marginTop === 0) {
    return '0px'
  }
  if (props.marginTop) {
    return props.marginTop + 'px'
  }
  return '60px'
})

const computedGap = computed(() => {
  if (props.gap === 0) {
    return '0px'
  }
  if (props.gap) {
    return props.gap + 'px'
  }
  return '16px'
})

const listRef = ref<HTMLElement | null>(null)
const isAtStart = ref(true)
const isAtEnd = ref(false)

// 드래그 관련 상태
const isDragging = ref(false)
const startX = ref(0)
const startScrollLeft = ref(0)

const scrollLeft = () => {
  if (!listRef.value) return
  listRef.value.scrollBy({ left: -(props.scrollWidth || 260), behavior: 'smooth' })
}

const scrollRight = () => {
  if (!listRef.value) return
  listRef.value.scrollBy({ left: props.scrollWidth || 260, behavior: 'smooth' })
}

const checkScrollPosition = () => {
  if (!listRef.value) return

  isAtStart.value = listRef.value.scrollLeft <= 0
  isAtEnd.value =
    listRef.value.scrollLeft + listRef.value.clientWidth >= listRef.value.scrollWidth - 1
}

// 드래그 시작
const onMouseDown = (e: MouseEvent) => {
  if (!listRef.value) return

  isDragging.value = true
  startX.value = e.pageX - listRef.value.offsetLeft
  startScrollLeft.value = listRef.value.scrollLeft

  // 드래그 중 텍스트 선택 방지
  document.body.style.userSelect = 'none'
}

// 드래그 중
const onMouseMove = (e: MouseEvent) => {
  if (!isDragging.value || !listRef.value) return

  const x = e.pageX - listRef.value.offsetLeft
  const walk = (x - startX.value) * 1.5 // 스크롤 속도 조절

  listRef.value.scrollLeft = startScrollLeft.value - walk
  checkScrollPosition()
}

// 드래그 종료
const onMouseUp = () => {
  isDragging.value = false
  document.body.style.userSelect = ''
}

// 마우스가 요소를 벗어났을 때
const onMouseLeave = () => {
  if (isDragging.value) {
    isDragging.value = false
    document.body.style.userSelect = ''
  }
}

onMounted(() => {
  if (listRef.value) {
    listRef.value.addEventListener('scroll', checkScrollPosition)
    listRef.value.addEventListener('mousedown', onMouseDown)
    listRef.value.addEventListener('mousemove', onMouseMove)
    listRef.value.addEventListener('mouseup', onMouseUp)
    listRef.value.addEventListener('mouseleave', onMouseLeave)

    // 터치 이벤트도 지원 (모바일용)
    listRef.value.addEventListener('touchstart', (e) => {
      if (!listRef.value) return
      isDragging.value = true
      startX.value = e.touches[0].pageX - listRef.value.offsetLeft
      startScrollLeft.value = listRef.value.scrollLeft
    })

    listRef.value.addEventListener('touchmove', (e) => {
      if (!isDragging.value || !listRef.value) return
      const x = e.touches[0].pageX - listRef.value.offsetLeft
      const walk = (x - startX.value) * 1.5
      listRef.value.scrollLeft = startScrollLeft.value - walk
      checkScrollPosition()
    })

    listRef.value.addEventListener('touchend', onMouseUp)
    checkScrollPosition()
  }
})
</script>

<template>
  <div class="horizontal-list-container">
    <div
      v-if="props.title || props.showNavigation"
      class="horizontal-list-header title-md white"
      :class="{ 'title-sm': isMobile }"
    >
      <div v-if="props.title" class="horizontal-list-header-title">{{ props.title }}</div>
      <div v-if="props.showNavigation" class="horizontal-list-header-button">
        <img
          class="horizontal-list-header-button-left svg-24"
          :class="{ 'svg-gray': isAtStart, 'svg-white': !isAtStart }"
          :src="ChevronLeftIcon"
          alt="chevron-left"
          @click="scrollLeft"
        />
        <img
          class="horizontal-list-header-button-right svg-24"
          :class="{ 'svg-gray': isAtEnd, 'svg-white': !isAtEnd }"
          :src="ChevronRightIcon"
          alt="chevron-right"
          @click="scrollRight"
        />
      </div>
    </div>
    <div class="horizontal-list" ref="listRef">
      <slot class="horizontal-list-item"></slot>
    </div>
  </div>
</template>

<style scoped>
.horizontal-list-container {
  width: 100%;
  margin-top: v-bind(computedMarginTop);
}

.horizontal-list-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.horizontal-list {
  width: 100%;
  display: flex;
  flex-direction: row;
  gap: v-bind(computedGap);
  margin-top: 16px;
  overflow-x: hidden;
}

.horizontal-list > * {
  flex-shrink: 0;
  cursor: pointer;
}

.horizontal-list-header-button {
  display: flex;
  flex-direction: row;
  gap: 20px;
}

.horizontal-list-header-button-left {
  cursor: pointer;
}

.horizontal-list-header-button-right {
  cursor: pointer;
}

@media (max-width: 1365px) {
  .horizontal-list-container {
    padding: 0px 0px 0px 20px;
  }

  .horizontal-list {
    padding-right: 20px;
  }
}
</style>
